import {
  type AgendaDataUpdater,
  type DragData,
  dragInsertPos,
  type DragInsertPos,
  insertClasses,
  isDataForSelf,
  matchingDragType,
} from "./dragDrop";
import { useMeetingEditor } from "./MeetingEditorContext";
import {
  useImmediateMutationSystem,
  type Mutator,
} from "@/lib/queuedMutations";
import { useEffect, useRef, useState } from "react";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import {
  AddNew,
  CollapseChevron,
  DragGrip,
  EditPencil,
  PullIn,
  RemoveX,
} from "./iconButtons";
import { useDndContext, useDraggable, useDroppable } from "@dnd-kit/core";
import { type Collapsible, useIndTracker, userName } from "./utilities";
import { ChevronRightIcon, CircleCheckIcon } from "lucide-react";
import { cn } from "@repo/shared/utils";
import { multiline } from "@repo/ui/components/utilities/multiline";
import { MeetingEditorDocumentLoader } from "./MeetingEditorDocument";
import { useGetAgendaItem } from "@/hooks/queryHooks/agendaItemHooks";
import {
  agendaItemRepo,
  type AgendaItemDto,
} from "@/repositories/agendaItemRepo";

// #region Item Loader

/** Loaders are necessary to load the object's data and only render the actual component when it
 * finishes loading because useImmediateMutationSystem requires the data be immediately
 * available in the render function. */

export const MeetingEditorAgendaItemLoader = ({
  ind,
  containerId,
  updateOrder,
  agendaItemId,
  onAdd,
  onRemove,
  index,
}: {
  ind: number;
  containerId: string;
  updateOrder: AgendaDataUpdater;
  agendaItemId: string;
  onAdd?: (afterAgendaItemId: string, load: boolean) => void;
  onRemove?: (
    agendaItemId: string,
    alsoDelete?: boolean
  ) => void | Promise<void>;
  index: number;
}) => {
  const { tenant } = useMeetingEditor();
  const { data: agendaItem } = useGetAgendaItem(tenant.id, agendaItemId);

  if (!agendaItem) return <p>...</p>;
  if (agendaItem._deleted) return null;

  return (
    <MeetingEditorAgendaItem
      ind={ind}
      containerId={containerId}
      updateOrder={updateOrder}
      agendaItem={agendaItem}
      onAdd={onAdd}
      onRemove={onRemove}
      index={index}
    />
  );
};

// #endregion

// #region Agenda Item

export const MeetingEditorAgendaItem = ({
  ind,
  containerId,
  updateOrder,
  agendaItem: _agendaItem,
  onAdd,
  onRemove,
  index,
}: {
  ind: number;
  containerId: string;
  updateOrder: AgendaDataUpdater;
  agendaItem: AgendaItemDto;
  onAdd?: (afterAgendaItemId: string, load: boolean) => void;
  onRemove?: (
    agendaItemId: string,
    alsoDelete?: boolean
  ) => void | Promise<void>;
  index: number;
}) => {
  const {
    tenant,
    mutationSystem,
    viewOnly,
    openEditForm,
    openLoadForm,
    collapseSet,
    updateAllCollapsed,
    undoStack,
    additionalDetails,
    userMap,
  } = useMeetingEditor();

  const mutationKey = "agendaItem:" + _agendaItem.id;
  const tenantId = tenant.id;
  const agendaItemMutation: Mutator<AgendaItemDto> = useFutureCallback(
    async (id, data) => {
      await agendaItemRepo.update(tenantId, id, data);
    }
  );
  const agendaItem = useImmediateMutationSystem(
    mutationSystem,
    mutationKey,
    _agendaItem
  );

  const canCollapse = !!agendaItem.documentIds.length;
  const [collapsed, _setCollapsed] = useState(false);
  const setCollapsed: typeof _setCollapsed = useFutureCallback((collapsed) => {
    if (canCollapse) {
      _setCollapsed(collapsed);
      updateAllCollapsed();
    }
  });

  const agendaItemId = agendaItem.id;
  const handleUpdateOrder = useFutureCallback(
    (updater: (documentIds: string[]) => string[]) => {
      const documentIds = updater(agendaItem.documentIds);
      mutationSystem.debounce(
        mutationKey,
        agendaItemMutation,
        { documentIds },
        agendaItemId
      );
    }
  );

  // drag-and-drop data
  const containerRef = useRef<HTMLElement | null>(null);

  const dndData: DragData = {
    id: agendaItem.id,
    ind,
    type: "agendaItem",
    container: containerId,
    updateOrder,
    index,
    overlay: (
      <div
        className="flex flex-row gap-2 py-2 pl-8"
        style={{
          width: containerRef.current?.offsetWidth,
          height: containerRef.current?.offsetHeight,
        }}
      >
        <DragGrip />
        <div className="flex-grow px-2 rounded-md">
          <div className="flex flex-row min-h-8">
            <p className="text-base mt-1 mr-2">{agendaItem.name}</p>
          </div>
        </div>
      </div>
    ),
  };

  const { active, over } = useDndContext();

  const isDraggingSelf = isDataForSelf(active, dndData);
  const isOverSelf = isDataForSelf(over, dndData);
  const insert: DragInsertPos = isOverSelf ? dragInsertPos(active, over) : null;
  const dndId = dndData.id + ":" + dndData.container + ":" + dndData.ind;

  const {
    attributes,
    listeners,
    setNodeRef: setDraggable,
  } = useDraggable({
    id: dndId,
    data: dndData,
    disabled: viewOnly,
  });

  const { setNodeRef: setDroppable } = useDroppable({
    id: dndId,
    data: dndData,
    disabled: viewOnly || !matchingDragType(active, "agendaItem"),
  });

  const style = {
    opacity: isDraggingSelf ? 0 : 1,
  };

  const setNodeRef = (ref: HTMLElement | null) => {
    setDraggable(ref);
    setDroppable(ref);
    containerRef.current = ref;
  };

  // handlers
  const handleEdit = useFutureCallback(() =>
    openEditForm("agendaItem", agendaItem)
  );
  const handleRemoveAgendaItem = useFutureCallback((alsoDelete: boolean) => {
    if (alsoDelete) {
      // delete this item
      mutationSystem.debounce(
        mutationKey,
        agendaItemMutation,
        { _deleted: true },
        agendaItemId
      );
    }

    onRemove?.(agendaItemId, alsoDelete);
  });

  const addDocument = (documentId: string, load: boolean) => {
    const prev = agendaItem.documentIds.slice();
    const next = [...prev, documentId];

    mutationSystem.debounce(
      mutationKey,
      agendaItemMutation,
      { documentIds: next },
      agendaItemId
    );

    // push delta to undo stack
    undoStack.push({
      label: load ? "Attach Existing Document" : "Add New Document",
      undo() {
        mutationSystem.debounce(
          mutationKey,
          agendaItemMutation,
          { documentIds: prev },
          agendaItemId
        );
      },
      redo() {
        mutationSystem.debounce(
          mutationKey,
          agendaItemMutation,
          { documentIds: next },
          agendaItemId
        );
      },
    });
  };

  const handleAddDocument = useFutureCallback(async () => {
    const newDocument = await openEditForm("document", null);
    if (!newDocument) return;

    addDocument(newDocument.id, false);
  });

  const handleLoadDocument = useFutureCallback(async () => {
    const newDocument = await openLoadForm("document");
    if (!newDocument) return;

    addDocument(newDocument.id, true);
  });

  const handleCreateAgendaItem = useFutureCallback(() => {
    onAdd?.(agendaItemId, false);
  });
  const handleLoadAgendaItem = useFutureCallback(() => {
    onAdd?.(agendaItemId, true);
  });

  // collapse / expand button
  const toggleCollapsed = useFutureCallback(() => {
    setCollapsed((collapsed) => !collapsed);
  });
  const isCollapsed = useFutureCallback(() => collapsed);
  const getCanCollapse = useFutureCallback(() => canCollapse);
  const id = agendaItem.id;

  useEffect(() => {
    const collapsible: Collapsible = {
      id,
      type: "agendaItem",
      canCollapse: getCanCollapse,
      isCollapsed,
      setCollapsed,
    };

    collapseSet.add(collapsible);
    return () => {
      collapseSet.delete(collapsible);
    };
  }, [getCanCollapse, isCollapsed, setCollapsed, collapseSet, id]);

  const handleRemoveDocument = useFutureCallback((documentId: string) => {
    const prev = agendaItem.documentIds.slice();
    const next = prev.filter((id) => id !== documentId);

    mutationSystem.debounce(
      mutationKey,
      agendaItemMutation,
      { documentIds: next },
      agendaItemId
    );

    // push delta to undo stack
    undoStack.push({
      label: "Remove Document",
      undo() {
        mutationSystem.debounce(
          mutationKey,
          agendaItemMutation,
          { documentIds: prev },
          agendaItemId
        );
      },
      redo() {
        mutationSystem.debounce(
          mutationKey,
          agendaItemMutation,
          { documentIds: next },
          agendaItemId
        );
      },
    });
  });

  const requiresApproval =
    agendaItem.approver?.approverType == "team" ||
    agendaItem.approver?.approverType === "user";

  const icon = requiresApproval ? (
    <CircleCheckIcon className="w-6" />
  ) : (
    <ChevronRightIcon className="w-6" />
  );

  const { getInd, trackInd } = useIndTracker();

  return (
    <div
      className={cn(
        "dnd relative flex flex-col rounded-md bg-white/80 pl-8",
        insertClasses(insert)
      )}
      style={style}
      ref={setNodeRef}
    >
      <div
        className={cn(
          "flex flex-row gap-2 group py-2",
          collapsed && "border-b-2 border-dashed border-gray-200 mb-2"
        )}
      >
        <DragGrip
          disabled={viewOnly}
          titleNote={
            requiresApproval ? "Item Requires Approval" : "No Approval Required"
          }
          icon={icon}
          {...attributes}
          {...listeners}
        />
        <div className="flex-grow px-2 hover:bg-gray-600/5 rounded-md">
          <div className="flex flex-row min-h-8 gap-x-2">
            <p className="text-base mt-1 mr-2">{agendaItem.name}</p>
            {!viewOnly && (
              <>
                <EditPencil title="Edit Agenda Item" onClick={handleEdit} />
                {onRemove && (
                  <RemoveX
                    type="item"
                    title="Remove Item From Section"
                    label={agendaItem.name}
                    onClick={handleRemoveAgendaItem}
                    allowDelete
                  />
                )}
                {onAdd && (
                  <>
                    <AddNew
                      type="agendaItem"
                      title="Create new item below"
                      onClick={handleCreateAgendaItem}
                    />
                    <PullIn
                      type="agendaItem"
                      title="Add existing item below"
                      onClick={handleLoadAgendaItem}
                    />
                  </>
                )}
                <AddNew
                  type="document"
                  title="Create and attach new document"
                  onClick={handleAddDocument}
                />
                <PullIn
                  type="document"
                  title="Attach existing document"
                  onClick={handleLoadDocument}
                />
              </>
            )}
            <div className="flex-grow" />
            {canCollapse && (
              <CollapseChevron
                title="Item"
                collapsed={collapsed}
                onClick={toggleCollapsed}
              />
            )}
          </div>

          {agendaItem.description && (
            <p className="text-gray-400 text-sm pl-9">
              {multiline(agendaItem.description)}
            </p>
          )}
          {additionalDetails &&
            (agendaItem.comment ||
              agendaItem.script ||
              (agendaItem.presenter &&
                (typeof agendaItem.presenter === "string" ||
                  agendaItem.presenter.userId !== "none")) ||
              agendaItem.motion) && (
              <div className="text-gray-400 text-sm pl-9">
                {agendaItem.comment && (
                  <p className="mt-2">
                    <strong>Staff comment:</strong>{" "}
                    <em>{multiline(agendaItem.comment)}</em>
                  </p>
                )}
                {agendaItem.script && (
                  <p className="mt-2">
                    <strong>Script:</strong>{" "}
                    <em>{multiline(agendaItem.script)}</em>
                  </p>
                )}
                {agendaItem.presenter &&
                  (typeof agendaItem.presenter === "string" ||
                    agendaItem.presenter.userId !== "none") && (
                    <p className="mt-2">
                      <strong>Presenter:</strong>{" "}
                      <em>
                        {typeof agendaItem.presenter === "string"
                          ? userName(agendaItem.presenter, userMap)
                          : agendaItem.presenter.manualEntry ??
                            userName(agendaItem.presenter.userId, userMap)}
                      </em>
                    </p>
                  )}
                {agendaItem.motion && (
                  <p className="mt-2">
                    <strong>Motion:</strong>{" "}
                    <em>{multiline(agendaItem.motion)}</em>
                  </p>
                )}
              </div>
            )}
        </div>
      </div>

      <div className={cn("flex flex-col pl-12", collapsed && "hidden")}>
        {agendaItem.documentIds?.map((documentId, index) => (
          <MeetingEditorDocumentLoader
            key={documentId + ":" + getInd(documentId)}
            ind={trackInd(documentId)}
            containerId={containerId + "/" + ind + "/" + agendaItemId}
            updateOrder={handleUpdateOrder}
            index={index}
            documentId={documentId}
            onRemove={handleRemoveDocument}
          />
        ))}
      </div>
    </div>
  );
};

// #endregion
